import axios from "axios";
import { authHeader } from "./authHeader";
import { BASE_URL, CLIENT_TIMEOUT_MS } from "../base";
import { toast } from "react-toastify";
import type { Id } from "react-toastify";
import { t } from "i18next";
import { NO_DETAIL_MESSAGE, UnauthorizedError } from "./apiUtils";
import { isApiRequest } from "./requestOrigin";
import {
  buildFailedRequestRecord,
  recordFailedRequest,
} from "../components/common/lastFailedRequest";
import type { FailedRequestRecord } from "../components/common/lastFailedRequest";
import { openReportProblem } from "../components/common/reportProblemBus";

type ApiErrorData = {
  detail?: string; // backend: {"detail": e.message}
  [key: string]: unknown;
};

type NormalizedError = {
  message: string;
  status?: number;
  data?: ApiErrorData;
};

const configUrl = BASE_URL;
const timeout = CLIENT_TIMEOUT_MS ? parseInt(CLIENT_TIMEOUT_MS, 10) : 30000;

const axiosInstance = axios.create({
  baseURL: configUrl,
  timeout: timeout,
});

axiosInstance.defaults.headers.common["Content-Type"] =
  "application/json;charset=UTF-8";
axiosInstance.defaults.headers.common["accept"] =
  "application/json, text/plain, */*";

// Request interceptor to inject the token into headers dynamically to handle token refreshes.
// The token and the language header are for the Akili API only: a request that resolves to any
// other origin (axios uses an absolute `url` as it is) is returned untouched, so the user's
// credential never reaches a third party (AKL-1110). Such a request also skips `authHeader()`,
// whose missing-token path signs the user out.
axiosInstance.interceptors.request.use(
  (config) => {
    if (!isApiRequest(config.url, config.baseURL, configUrl)) {
      return config;
    }
    const token = authHeader();
    if (token) {
      config.headers["Authorization"] = token;
    }
    const languagePreference = localStorage.getItem("language") || "en";
    config.headers["Language-Preference"] = languagePreference;
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * The two no-response toasts render their existing message alongside a Report this action
 * (AKL-798). The message strings are unchanged.
 *
 * The action dismisses **its own** toast, by the id `toast.error` returns: react-toastify's
 * container carries `--toastify-z-index: 9999`, far above the dialog's `z-[1400]`, so a live
 * toast paints over the dialog the click just opened. A bare `toast.dismiss()` takes an id and
 * with none clears *every* container — so an unrelated upload-success toast vanished unread
 * because the user clicked "Report this" on a network error. `closeOnClick` defaults to true
 * and covers the click itself; these two toasts still have no dedupe id and can stack, so a
 * SECOND toast can arrive over an open dialog — a known rough edge, left alone rather than
 * changing dedupe behaviour for every existing failure.
 *
 * A toast carrying a clickable element and a non-default `autoClose` is a first in this repo
 * (there is no other `autoClose` usage and no other custom toast content), so the rendering
 * is proven by neither precedent nor CI and has to be checked by hand.
 * The failure is passed in rather than read back from `lastFailedRequest`: that store is
 * keep-first, so with two failures on screen a click on the second toast would attach the
 * first one's request while showing the second one's message. A toast is about one specific
 * failure and carries it.
 * @param message the already-translated toast message
 * @param failedRequest the failure this toast is about
 * @returns the id of the raised toast
 */
const showReportableToast = (
  message: string,
  failedRequest: FailedRequestRecord,
): Id => {
  // Declared before the content so the action's closure can capture it; it is assigned by the
  // time any click can happen, and the guard keeps an unassigned id from becoming a
  // dismiss-everything call.
  let toastId: Id | undefined;
  toastId = toast.error(
    <span>
      {message}{" "}
      <button
        type="button"
        className="font-semibold underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        onClick={() => {
          if (toastId !== undefined) toast.dismiss(toastId);
          openReportProblem("toast", failedRequest);
        }}
      >
        {t("Report this")}
      </button>
    </span>,
    // 10 s rather than the default 5 s, so the action is actually clickable.
    { autoClose: 10000 },
  );
  return toastId;
};

// Add a response interceptor
axiosInstance.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    // Record the failure for the Report a problem dialog (AKL-798) BEFORE anything else.
    // Two reasons for the position: the 5xx branch below assigns `window.location.href`,
    // a document navigation past which nothing not already in storage survives; and doing
    // it above the `skipGlobalErrorHandler` return means the opted-out requests (the
    // migration and export polls) contribute too, which is right — their failures are
    // exactly what a user would report. The only effect on those callers is a
    // sessionStorage write: no branch, no return value, no toast change.
    //
    // `error.config` can be absent (a request-interceptor rejection has no config), which
    // is why the line below already reads `error.config && …`; read through optional
    // chaining and record what is available.
    const failedUrl: string | undefined = error.config?.url;
    const failedStatus: number | undefined = error.response?.status;
    // Only failures worth reporting are recorded: a 5xx, or an error with no response at all
    // (timeout / network). A 4xx is the server answering correctly about a bad or
    // unauthorised request, and the 401 branch a few lines below is the sharp case — it
    // clears the token and bounces an expired session to login. Recording that wrote
    // `GET /users/me 401` into the keep-first slot, where it sat for the whole five-minute
    // window; sign back in, hit a real 500 inside it, and keep-first kept the 401, so support
    // received the session expiry instead of the failure being reported.
    //
    // This replaces the blanket `/users/me` skip that used to live here (removed because it
    // dropped the most likely 5xx the feature exists to capture). Filtering on what went
    // wrong rather than on which endpoint it was is the better rule: `/users/me` 500s are
    // recorded, `/users/me` 401s are not, and no endpoint list has to be maintained.
    const reportable = failedStatus === undefined || failedStatus >= 500;
    // Never let a failed report record itself as the failure to report.
    const isFeedbackCall =
      failedUrl !== undefined && failedUrl.includes("/feedback");
    // Built once here so the stored record and the toast's own copy cannot disagree.
    const failedRequestRecord = (): FailedRequestRecord =>
      buildFailedRequestRecord({
        url: failedUrl,
        method: error.config?.method,
        status: failedStatus,
        code: error.response ? undefined : error.code,
      });

    if (reportable && !isFeedbackCall) {
      recordFailedRequest({
        url: failedUrl,
        method: error.config?.method,
        status: failedStatus,
        code: error.response ? undefined : error.code,
      });
    }

    if (error.config && error.config.skipGlobalErrorHandler) {
      // Bypass global error handling for specific requests
      return Promise.reject(error);
    }
    if (error.response) {
      const status: number = error.response.status;
      const data: ApiErrorData = (error.response.data ?? {}) as ApiErrorData;

      // Session teardown for a 401 only. The API answers 401 when the token is missing,
      // expired or unparseable (`authentication/keycloak.py` raises 401 on
      // ExpiredSignatureError; `core/middleware.py` answers 401 when no user resolved), so
      // a 401 means the session is over: the token is cleared, and the next request signs
      // the user out through authHeader(). A 403 is different in kind — a live session
      // asked for something it may not have (`PermissionError` → `403 Forbidden`) — and
      // falls through to the ordinary normalized 4xx rejection below with the token
      // untouched. Clearing it here signed a user out on their next click for opening a
      // page they were not allowed to see (AKL-939). AKL-895 had met the same bug on
      // `end_upload` and opted that one call out with `suppressForbiddenHandling`; with
      // the rule itself fixed the flag is gone.
      if (status === 401) {
        localStorage.removeItem("token");
        localStorage.setItem(
          "unauthorizedMessage",
          data.detail || t("Unauthorized access"),
        );

        throw new UnauthorizedError(
          "Unauthorized",
          data?.detail || t("Unauthorized access"),
        );
      }

      // surface ALL other 4xx errors
      if (status >= 400 && status < 500) {
        const normalized: NormalizedError = {
          message: data.detail || NO_DETAIL_MESSAGE,
          status,
          data,
        };
        return Promise.reject(normalized);
      }

      // Keep existing behavior for everything else (likely 5xx), unless the
      // caller opted a specific request out of the hard redirect.
      if (error.config?.suppressUnexpectedErrorRedirect) {
        const normalized: NormalizedError = {
          message: data.detail || NO_DETAIL_MESSAGE,
          status,
          data,
        };
        return Promise.reject(normalized);
      }
      // Already on the error page: reject rather than assign the same URL again. That
      // assignment is a document navigation, so re-triggering it reloads the page under the
      // user — and on `/unexpectedError` that is not hypothetical, because `ProfileProvider`
      // fetches `/users/me` there, which fails for the same reason the 5xx did. The page
      // would reload in a loop and the "Report this problem" button would be unreachable in
      // exactly the outage it exists for (AKL-798). Rejecting instead lets ProfileProvider's
      // own catch run, so the header renders and the button stays clickable.
      if (window.location.pathname !== "/unexpectedError") {
        window.location.href = "/unexpectedError";
        return;
      }
      return Promise.reject({
        message: data.detail || NO_DETAIL_MESSAGE,
        status,
        data,
      } as NormalizedError);

      // Request was sent but no response was received (timeout)
    } else if (error.code === "ECONNABORTED") {
      const msg = t("Server timeout error");
      showReportableToast(msg, failedRequestRecord());
      const normalized: NormalizedError = {
        message: msg,
        data: {
          detail: msg,
        },
      };
      return Promise.reject(normalized);

      // Network Error
    } else if (error.code === "ERR_NETWORK") {
      const msg = t("Network Error");
      showReportableToast(msg, failedRequestRecord());
      const normalized: NormalizedError = {
        message: msg,
        data: {
          detail: msg,
        },
      };
      return Promise.reject(normalized);
    }

    // Fallback
    return Promise.reject(error);
  },
);

export default axiosInstance;
